import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { uuidv4, returnFlightDate } from '../../../utils/utility';
import {
  AIRPLANE_TRANSPORTATION,
  numberWithCommas,
} from '../../../utils/consts';
import { SliderGallery, Tooltip, Accordion } from '../../../component';
import {
  BusIcon,
  HotelIcon,
  InfoCircleIcon,
  PlusCircleIcon,
  CheckIcon,
  UsdCircleIcon,
} from '~svg';
import { fileHandlerUrl } from '../../../utils/consts/server';
const PRICE_TABEL_HEADER = [
  'booking.roomtype',
  'tour.agerange',
  'language.rial',
  'language.usd',
  'language.eur',
];
const TourDetail = ({
  intl = {},
  language = {},
  tourData = {},
  isIdRequired = true,
  switchToGrid = () => {},
}) => {
  const { formatMessage } = intl;
  const [_id, set_id] = useState('');
  const [images, setImages] = useState([]);
  const [tourInfo, setTourInfo] = useState([]);
  const [description, setDescription] = useState([]);
  const [tourTransport, setTourTransport] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [tourResidency, setTourResidency] = useState([]);
  const [pricing, setPricing] = useState({});
  const [pricingBody, setPricingBody] = useState({});
  const HEADER_ICONS = {
    info: InfoCircleIcon,
    transportation: BusIcon,
    residency: HotelIcon,
    facilities: PlusCircleIcon,
    pricing: UsdCircleIcon,
  };
  useEffect(() => {
    const {
      images,
      tourInfo,
      description,
      tourTransport,
      facilities,
      residency,
      pricing,
      _id,
    } = tourData;
    if (_id || !isIdRequired) {
      const updatedPricing = addNameFieldToPricing(residency, pricing);
      set_id(_id || '');
      setTourInfo(tourInfo || []);
      setDescription(description || {});
      setFacilities(facilities || []);
      setTourTransport(tourTransport || []);
      setTourResidency(residency || []);
      setPricing(updatedPricing || {});
      setImages(images || []);
    } else {
      switchToGrid();
    }
  }, [tourData]);
  useEffect(() => {
    // pricing data includes nested objects and arrays and using it to display data will be complicating. in this useEffect an array of objects will be created for each hotel so all data of each room align together for easier way of displaying
    let pricingBody = {};
    pricing?.hotels?.map((hotel) => {
      let hotelPricingData = [];
      hotel?.rooms?.map(({ roomType, passengerAgeRange, price }) => {
        let currentHotelPricing = [
          roomType,
          `${passengerAgeRange?.min} - ${passengerAgeRange?.max}`,
        ];
        price?.map(({ amount }) =>
          currentHotelPricing?.push(numberWithCommas(amount)),
        );
        hotelPricingData?.push(currentHotelPricing);
      });
      pricingBody = { ...pricingBody, [hotel?.hotelId]: hotelPricingData };
    });
    setPricingBody(pricingBody);
  }, [pricing]);
  const addNameFieldToPricing = (residency = [], pricing = {}) => {
    let { hotels, description } = pricing;
    let hotelsList = {};
    residency?.map(({ hotel: { _id, name } }) => {
      hotelsList = { ...hotelsList, [`${_id}`]: name };
    });
    hotels = hotels?.map((item) => {
      return { ...item, name: hotelsList?.[item?.hotelId] };
    });
    return { hotels, description };
  };
  const setTransportationType = (transportType) =>
    transportType._id === AIRPLANE_TRANSPORTATION?._id ||
    transportType?.name?.en === AIRPLANE_TRANSPORTATION?.name?.en;

  const renderGridElement = ({ titleId, value }) => {
    return (
      <div className="w-100 col-sm-6 d-flex align-items-center p-0 p-1">
        <span className="d-flex text-third font-weight-bold font-msmall mx-1 text-nowrap p-0">
          {formatMessage({ id: titleId })}:
        </span>
        <Tooltip
          position="top"
          content={value}
          direction={language.direction}
          backgroundColor="warning"
          textColor="dark"
          customClassName="text-truncate"
        >
          <span className="text-primary font-small text-truncate">{value}</span>
        </Tooltip>
      </div>
    );
  };
  const renderGridBox = (
    titleId = '',
    iconName = 'info',
    gridContent = <></>,
  ) => {
    return (
      <section className="w-100 d-flex flex-column bg-white border default-border-radius-small mb-4 p-2">
        <h6 className="w-100 d-flex flex-wrap align-items-center border-bottom py-2 m-0 text-secondary">
          <>
            {React.createElement(HEADER_ICONS?.[iconName], {
              color: '#00798c',
              variant: 'svg-small',
            })}
          </>
          <span className="mx-2">{formatMessage({ id: titleId })}</span>
        </h6>
        <div className="w-100 d-flex flex-wrap align-items-center mt-3">
          {gridContent}
        </div>
      </section>
    );
  };
  return (
    <main className="w-100 d-flex flex-column px-3 gap-10">
      {renderGridBox(
        'tour.tourinformationTitle',
        'info',
        <>
          {renderGridElement({
            titleId: 'tour.domesticName',
            value: tourInfo.name?.fa,
          })}
          {renderGridElement({
            titleId: 'tour.englishTourName',
            value: tourInfo.name?.en,
          })}
          {renderGridElement({
            titleId: 'booking.startdate',
            value: returnFlightDate(tourInfo.startDate, language?.name, true),
          })}
          {renderGridElement({
            titleId: 'booking.enddate',
            value: returnFlightDate(tourInfo.endDate, language?.name, true),
          })}
          {renderGridElement({
            titleId: 'tour.starttime',
            value: tourInfo.startTime,
          })}
          {renderGridElement({
            titleId: 'tour.endtime',
            value: tourInfo.endTime,
          })}
        </>,
      )}
      <section className="mb-4 px-1">
        <SliderGallery
          slidesList={images}
          language={language}
          formatMessage={formatMessage}
          height={180}
          width={180}
        />
      </section>
      {renderGridBox(
        'ticket.description',
        'info',
        <div className="w-100 d-flex flex-column">
          <div className="w-100 d-flex p-0 p-1 border-bottom">
            <span className="d-flex text-third font-weight-bold font-msmall mx-1 text-nowrap p-0">
              {formatMessage({ id: 'tour.fa.description' })}:
            </span>
            <p className="d-flex text-primary font-small text-wrap">
              {description?.fa}
            </p>
          </div>
          <div className="w-100 d-flex p-0 p-1 mt-3">
            <span className="d-flex text-third font-weight-bold font-msmall mx-1 text-nowrap p-0">
              {formatMessage({ id: 'tour.en.description' })}:
            </span>
            <p className="d-flex text-primary font-small text-wrap">
              {description?.en}
            </p>
          </div>
        </div>,
      )}
      {renderGridBox(
        'tour.facilities',
        'facilities',
        <ul className="w-100 d-flex flex-wrap list-unstyled m-0 mt-3 p-0">
          {facilities.map((facility) => {
            const id = uuidv4();
            return (
              <li
                key={id}
                className="w-100 col-sm-6 col-lg-4 d-flex flex-wrap align-items-center justify-content-start gap-5px p-1 mb-2 text-capitalize"
              >
                {facility.iconName ? (
                  <img
                    alt="icon"
                    src={`${fileHandlerUrl}/file/show/${facility.iconName}`}
                    className="w-tour-images-preview h-tour-images-preview d-flex align-items-cenetr justify-content-center rounded"
                  />
                ) : (
                  <span className="d-flex align-items-center justify-content-center p-1 bg-dark rounded-circle">
                    <CheckIcon color="#fff" size={12} />
                  </span>
                )}
                <span>{facility?.name?.[language.name]}</span>
              </li>
            );
          })}
        </ul>,
      )}
      {renderGridBox(
        'profile.transportation',
        'transportation',
        <>
          {tourTransport.map((transport, index) => {
            const isTransportationFlight = setTransportationType(
              transport?.transportType,
            );
            return (
              <div
                key={uuidv4()}
                className={`w-100 d-flex flex-wrap align-items-center mt-3 ${
                  index !== tourTransport?.length - 1 ? 'border-bottom' : ''
                }`}
              >
                {renderGridElement({
                  titleId: 'booking.origin',
                  value: transport?.origin?.name?.[language?.name],
                })}
                {renderGridElement({
                  titleId: 'booking.destination',
                  value: transport?.destination?.name?.[language?.name],
                })}

                {renderGridElement({
                  titleId: 'booking.startdate',
                  value: returnFlightDate(
                    transport?.startDate,
                    language?.name,
                    true,
                  ),
                })}
                {renderGridElement({
                  titleId: 'tour.starttime',
                  value: transport?.startTime,
                })}
                {renderGridElement({
                  titleId: 'booking.returndate',
                  value: returnFlightDate(
                    transport?.arrivalDate,
                    language?.name,
                    true,
                  ),
                })}
                {renderGridElement({
                  titleId: 'booking.arrives',
                  value: transport?.arrivalTime,
                })}
                {renderGridElement({
                  titleId: 'booking.baggages',
                  value: transport?.baggages,
                })}
                {renderGridElement({
                  titleId: 'tour.transportTypes',
                  value: transport?.transportType?.name?.[language?.name],
                })}
                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'profile.airlinename'
                    : 'tour.transportproviderlocal',
                  value: transport?.transportProvider?.name?.fa,
                })}
                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'profile.airlinename'
                    : 'tour.transportprovideren',
                  value: transport?.transportProvider?.name?.en,
                })}
                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'booking.airplanetype'
                    : 'tour.vehiclenamefa',
                  value: transport?.vehicleName?.name?.fa,
                })}
                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'booking.airplanetype'
                    : 'tour.vehiclenameen',
                  value: transport?.vehicleName?.name?.en,
                })}

                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'tour.originairport'
                    : 'tour.originterminalfa',
                  value: transport?.originTerminal?.name?.fa,
                })}
                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'tour.originairport'
                    : 'tour.originterminalen',
                  value: transport?.originTerminal?.name?.en,
                })}
                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'tour.destinationairport'
                    : 'tour.destinationterminalfa',
                  value: transport?.arrivalTerminal?.name?.fa,
                })}
                {renderGridElement({
                  titleId: isTransportationFlight
                    ? 'tour.destinationairport'
                    : 'tour.destinationterminalen',
                  value: transport?.arrivalTerminal?.name?.en,
                })}
                {isTransportationFlight &&
                  renderGridElement({
                    titleId: 'booking.flightno',
                    value: transport?.flightNumber,
                  })}
                {renderGridElement({
                  titleId: 'tour.description',
                  value: transport?.description,
                })}
              </div>
            );
          })}
        </>,
      )}
      {renderGridBox(
        'profile.inhabitancy',
        'residency',
        <>
          {tourResidency.map((residency, index) => {
            return (
              <div
                key={uuidv4()}
                className={`w-100 d-flex flex-wrap align-items-center mt-3 ${
                  index !== tourResidency?.length - 1 ? 'border-bottom' : ''
                }`}
              >
                {renderGridElement({
                  titleId: 'booking.city',
                  value: residency?.location?.name?.[language?.name],
                })}
                {renderGridElement({
                  titleId: 'booking.hotel',
                  value: residency?.hotel?.name?.[language?.name],
                })}
                {renderGridElement({
                  titleId: 'tour.arrivaldateto',
                  value: returnFlightDate(
                    residency?.entryDate,
                    language?.name,
                    true,
                  ),
                })}
                {renderGridElement({
                  titleId: 'booking.startdate',
                  value: residency?.entryTime,
                })}
                {renderGridElement({
                  titleId: 'booking.enddate',
                  value: returnFlightDate(
                    residency?.exitDate,
                    language?.name,
                    true,
                  ),
                })}
                {renderGridElement({
                  titleId: 'booking.departs',
                  value: residency?.exitTime,
                })}
                {renderGridElement({
                  titleId: 'mobileProfile.food&Drink',
                  value: residency?.foods?.join(' , '),
                })}
                {renderGridElement({
                  titleId: 'booking.roomtype',
                  value: residency?.rooms
                    ?.map(
                      ({ name, count }) =>
                        `${name?.[language?.name]} : ${count}`,
                    )
                    ?.join(' , '),
                })}
              </div>
            );
          })}
        </>,
      )}
      {renderGridBox(
        'profile.pricing',
        'pricing',
        <>
          {pricing?.hotels?.map(({ name, hotelId }) => (
            <div key={hotelId} className="w-100 d-flex flex-column mb-3">
              <Accordion
                id="pricing"
                classTitle="font-weight-bolder font-small"
                title={
                  name?.[language?.name] ? name?.[language?.name] : name?.fa
                }
                icon={<HotelIcon size={20} color={'#3d4a8f'} />}
              >
                <div className="w-100 d-flex flex-column">
                  <table className="table table-striped border px-1">
                    <thead>
                      <tr className="bg-secondary text-light">
                        {PRICE_TABEL_HEADER?.map((id) => (
                          <th key={uuidv4()} scope="col">
                            <span className="w-100 d-flex align-items-center font-msmall text-nowrap">
                              {intl?.formatMessage({ id })}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pricingBody?.[hotelId]?.map((roomData) => {
                        return (
                          <tr key={uuidv4()}>
                            {roomData?.map((data) => (
                              <td
                                key={uuidv4()}
                                className="text-truncate font-msmall"
                              >
                                {data?.[language.name]
                                  ? data?.[language.name]
                                  : data}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Accordion>
            </div>
          ))}
          <div className="w-100 d-flex flex-column">
            <div className="w-100 col-sm-6 col-lg-4 d-flex align-items-center p-0 p-1">
              <span className="d-flex text-third font-weight-bold font-msmall mx-1 text-nowrap p-0">
                {formatMessage({ id: 'tour.fa.description' })}:
              </span>
              <span className="w-100 d-flex text-primary font-small text-truncate">
                {pricing?.description?.fa}
              </span>
            </div>
            <div className="w-100 col-sm-6 col-lg-4 d-flex align-items-center p-0 p-1">
              <span className="d-flex text-third font-weight-bold font-msmall mx-1 text-nowrap p-0">
                {formatMessage({ id: 'tour.en.description' })}:
              </span>
              <span className="w-100 d-flex text-primary font-small text-truncate">
                {pricing?.description?.en}
              </span>
            </div>
          </div>
        </>,
      )}
    </main>
  );
};

TourDetail.propTypes = {
  intl: PropTypes.object,
  language: PropTypes.object,
  tourData: PropTypes.object,
  isIdRequired: PropTypes.bool,
  switchToGrid: PropTypes.func,
};
export default TourDetail;
